import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Clock, Award } from 'lucide-react';
import { Badge } from '../ui/badge';

// Hero images for different category types - mapped by source_category code
const CATEGORY_HERO_IMAGES = {
  // Banners
  'VNL': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  'BAN': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  'MSH': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  'FAB': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  
  // Signs & Displays
  'RGD': 'https://images.unsplash.com/photo-1563906267088-b029e7101114?w=1200&q=80',
  'ADH': 'https://images.unsplash.com/photo-1563906267088-b029e7101114?w=1200&q=80',
  'SEG': 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
  
  // Trade Show & Events
  'TDP': 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
  'TNT': 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200&q=80',
  'TBL': 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
  'BNS': 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
  
  // Flags & Outdoor
  'FLG': 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80',
  'FTH': 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80',
  'AFR': 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80',
  
  // Wall & Art
  'WAL': 'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=1200&q=80',
  
  // Default
  'default': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
};

// Category display names and descriptions
const CATEGORY_INFO = {
  'VNL': { name: 'Vinyl Banners', tagline: 'Durable outdoor & indoor vinyl banners' },
  'BAN': { name: 'Banners', tagline: 'Professional banners for every occasion' },
  'MSH': { name: 'Mesh Banners', tagline: 'Wind-resistant mesh for outdoor use' },
  'FAB': { name: 'Fabric Banners', tagline: 'Premium fabric displays' },
  'RGD': { name: 'Rigid Signs', tagline: 'Durable rigid signage solutions' },
  'ADH': { name: 'Adhesive Products', tagline: 'Custom decals, stickers & vinyl' },
  'SEG': { name: 'SEG Displays', tagline: 'Silicone edge graphics for stunning displays' },
  'TDP': { name: 'Trade Show', tagline: 'Complete trade show solutions' },
  'TNT': { name: 'Event Tents', tagline: 'Custom branded event tents' },
  'TBL': { name: 'Table Throws', tagline: 'Professional table displays' },
  'BNS': { name: 'Banner Stands', tagline: 'Portable retractable displays' },
  'FLG': { name: 'Flags', tagline: 'Custom flags & pennants' },
  'FTH': { name: 'Feather Flags', tagline: 'Eye-catching feather banners' },
  'AFR': { name: 'A-Frames', tagline: 'Sidewalk signs & displays' },
  'WAL': { name: 'Wall Art', tagline: 'Custom wall graphics & murals' },
  'CHL': { name: 'Chalk Boards', tagline: 'Reusable chalk display boards' },
  'LGF': { name: 'Large Format', tagline: 'Professional large format printing' },
};

const CategoryHero = ({ 
  category = null, 
  categoryCode = null,
  title = 'All Products',
  description = 'Browse our complete catalog of large format printing products',
  productCount = 0,
  showPromo = true 
}) => {
  // Determine the hero image based on category
  const code = categoryCode || category?.code || category?.source_category || 'default';
  const heroImage = CATEGORY_HERO_IMAGES[code] || CATEGORY_HERO_IMAGES['default'];
  const categoryInfo = CATEGORY_INFO[code];
  
  // Use category data if available, otherwise fallback to props
  const displayTitle = category?.name || categoryInfo?.name || title;
  const displayDescription = category?.description || categoryInfo?.tagline || description;

  return (
    <section className="relative overflow-hidden" data-testid="category-hero">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroImage}
          alt={displayTitle}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = CATEGORY_HERO_IMAGES['default'];
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory-900/90 via-ivory-900/70 to-ivory-900/40" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 py-12 md:py-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <Link 
            to="/LargePrint" 
            className="inline-flex items-center gap-2 text-ivory-300 hover:text-gold-400 mb-4 transition-colors font-body text-sm"
            data-testid="back-to-large-format"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Large Format
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            {/* Left: Title & Description */}
            <div className="max-w-2xl">
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3"
                data-testid="products-heading"
              >
                {displayTitle}
              </h1>
              <p className="text-lg md:text-xl text-ivory-200 font-body">
                {displayDescription}
              </p>
              
              {/* Product Count */}
              {productCount > 0 && (
                <p className="text-ivory-400 font-body mt-2 text-sm">
                  {productCount} product{productCount !== 1 ? 's' : ''} available
                </p>
              )}
            </div>
            
            {/* Right: Promo Badges */}
            {showPromo && (
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-gold-500 hover:bg-gold-600 text-white border-0 px-4 py-2 text-sm font-body flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Free Shipping Available
                </Badge>
                <Badge className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-2 text-sm font-body flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Fast Turnaround
                </Badge>
                <Badge className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-2 text-sm font-body flex items-center gap-2 hidden md:flex">
                  <Award className="w-4 h-4" />
                  Premium Quality
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryHero;
